import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  getAlerts, createAlert, updateAlert, deleteAlert, downloadBackup,
  restoreBackup, importarMonday,
} from '../api/client'
import PageHeader from '../components/PageHeader'

export default function Settings() {
  const { t } = useTranslation(['settings', 'common'])
  // Accounts moved to Administración > Cuentas; alerts are what's left here
  // that people come for most.
  const [tab, setTab] = useState('alerts')

  const tabBtn = (id, label) => (
    <button
      onClick={() => setTab(id)}
      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
        tab === id ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'
      }`}
    >
      {label}
    </button>
  )

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader
          title={t('settings.title')}
          subtitle={t('settings.subtitle')}
        />
      </div>

      <Link to="/admin/cuentas"
        className="flex items-center justify-between gap-3 mb-6 max-w-2xl px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors">
        <span>{t('cuentasMovidas')}</span>
        <span className="font-medium text-gray-900 shrink-0">{t('cuentasMovidasLink')} →</span>
      </Link>

      <div className="flex gap-2 mb-6">
        {tabBtn('alerts', t('tabs.alerts'))}
        {tabBtn('system', t('tabs.system'))}
      </div>

      {tab === 'alerts' ? <AlertsTab /> : <SystemTab />}
    </div>
  )
}

// ── System tab ──────────────────────────────────────────────────────────────

function SystemTab() {
  const { t } = useTranslation(['settings', 'common'])
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState('')

  const handleBackup = async () => {
    setDownloading(true)
    setError('')
    setDone('')
    try {
      const name = await downloadBackup()
      setDone(t('system.backupDone', { name }))
    } catch (e) {
      setError(e.response?.data?.detail || t('system.backupFailed'))
    }
    setDownloading(false)
  }

  return (
    <div className="space-y-6">
    <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-xl">
      <h2 className="text-sm font-semibold text-gray-900 mb-1">{t('system.backupTitle')}</h2>
      <p className="text-xs text-gray-500 mb-4">{t('system.backupIntro')}</p>
      {error && <p className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}
      {done && <p className="mb-3 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">{done}</p>}
      <button
        onClick={handleBackup}
        disabled={downloading}
        className="bg-gray-900 text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors"
      >
        {downloading ? t('system.backupDownloading') : t('system.backupButton')}
      </button>
    </div>
    <RestoreBackup />
    <MondayImport />
    </div>
  )
}

// Replace the whole database with a downloaded backup (routers/backup.py
// checks the file and keeps a copy of the current state on the server).
const PALABRA = 'RESTAURAR'

function RestoreBackup() {
  const { t } = useTranslation(['settings', 'common'])
  const [archivo, setArchivo] = useState(null)
  const [antes, setAntes] = useState(true)
  const [palabra, setPalabra] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState('')

  const restaurar = async () => {
    setBusy(true)
    setError('')
    setDone('')
    try {
      if (antes) await downloadBackup()
      const r = await restoreBackup(archivo, palabra)
      setDone(t('system.restoreDone', { archivo: r.archivo }))
      setTimeout(() => window.location.reload(), 3000)
    } catch (e) {
      setError(e.response?.data?.detail || t('system.restoreFailed'))
      setBusy(false)
    }
  }

  return (
    <div className="bg-white rounded-xl border border-red-200 p-6 max-w-xl">
      <h2 className="text-sm font-semibold text-gray-900 mb-1">{t('system.restoreTitle')}</h2>
      <p className="text-xs text-gray-500 mb-4">{t('system.restoreIntro')}</p>
      {error && <p className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}
      {done && <p className="mb-3 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">{done}</p>}
      <label className="block text-xs font-medium text-gray-700 mb-1">{t('system.restoreFile')}</label>
      <input type="file" accept=".db" disabled={busy}
        onChange={e => setArchivo(e.target.files?.[0] || null)}
        className="block w-full text-sm text-gray-600 mb-3 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border file:border-gray-200 file:bg-white file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-50" />
      <label className="flex items-center gap-2 text-sm text-gray-700 mb-3">
        <input type="checkbox" checked={antes} disabled={busy} onChange={e => setAntes(e.target.checked)} />
        {t('system.restoreDownloadFirst')}
      </label>
      <label className="block text-xs font-medium text-gray-700 mb-1">{t('system.restoreConfirmLabel', { palabra: PALABRA })}</label>
      <input value={palabra} disabled={busy} onChange={e => setPalabra(e.target.value)} placeholder={PALABRA}
        className="w-48 border border-gray-200 rounded-lg px-3 py-1.5 text-sm mb-4 block" />
      <button
        onClick={restaurar}
        disabled={busy || !archivo || palabra !== PALABRA}
        className="bg-red-600 text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
      >
        {busy ? t('system.restoring') : t('system.restoreButton')}
      </button>
    </div>
  )
}

// ── monday.com import (Mantenimiento) ───────────────────────────────────────
// Upload a board export, preview what the load would change (the server
// rolls it back), then apply the same file. Order matters: partes need
// their units, so the steps are numbered.

const IMPORT_TIPOS = ['vehiculos', 'partes']
// Summary entries worth a second look before applying: data the load would
// overwrite, delete, or skip.
const IMPORT_ALERTAS = new Set([
  'valores_cambiados', 'valores_borrados', 'columnas_retipadas',
  'partes_borrados', 'sin_unidad', 'con_otro_abierto', 'estados_cambiados', 'repetidas', 'otro_proyecto',
])

function ImportResumen({ resumen }) {
  const { t } = useTranslation('settings')
  const fila = (v) => (Array.isArray(v) ? v.filter(x => x != null).join(' · ') : String(v))
  return (
    <dl className="divide-y divide-gray-100 border border-gray-200 rounded-lg text-sm">
      {Object.entries(resumen).map(([k, v]) => {
        const lista = Array.isArray(v)
        const vacio = lista ? v.length === 0 : !v
        const alerta = IMPORT_ALERTAS.has(k) && !vacio
        const label = t(`system.import.keys.${k}`, { defaultValue: k })
        return (
          <div key={k} className={`px-3 py-2 ${alerta ? 'bg-amber-50' : ''}`}>
            {lista && !vacio ? (
              <details>
                <summary className="cursor-pointer flex justify-between gap-4">
                  <span className={alerta ? 'text-amber-800' : 'text-gray-600'}>{label}</span>
                  <span className="font-medium text-gray-900">{v.length}</span>
                </summary>
                <ul className="mt-2 max-h-48 overflow-y-auto text-xs text-gray-600 space-y-0.5">
                  {v.map((x, i) => <li key={i}>{fila(x)}</li>)}
                </ul>
              </details>
            ) : (
              <div className="flex justify-between gap-4">
                <span className={alerta ? 'text-amber-800' : 'text-gray-600'}>{label}</span>
                <span className="font-medium text-gray-900 text-right">
                  {lista ? 0 : typeof v === 'object' && v !== null
                    ? Object.entries(v).map(([a, b]) => `${a}: ${b}`).join(' · ') || '—'
                    : String(v)}
                </span>
              </div>
            )}
          </div>
        )
      })}
    </dl>
  )
}

function MondayImport() {
  const { t } = useTranslation(['settings', 'common'])
  const [tipo, setTipo] = useState('vehiculos')
  const [archivo, setArchivo] = useState(null)
  const [reemplazar, setReemplazar] = useState(false)
  const [preview, setPreview] = useState(null)
  const [aplicado, setAplicado] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [inputKey, setInputKey] = useState(0)   // bump to clear the file input

  // Any change to what would be loaded invalidates the preview.
  const cambiar = (fn) => (...args) => { fn(...args); setPreview(null); setAplicado(null); setError('') }

  const enviar = async (aplicar) => {
    setBusy(true)
    setError('')
    try {
      const r = await importarMonday(tipo, archivo, { reemplazar, aplicar })
      if (aplicar) {
        setAplicado(r.resumen)
        setPreview(null)
        setArchivo(null)
        setInputKey(k => k + 1)
      } else {
        setPreview(r.resumen)
      }
    } catch (e) {
      const d = e.response?.data?.detail
      if (d?.code === 'otro_tablero') {
        // Name the step the file belongs to, not just what it isn't.
        const esperado = t(`system.import.tipos.${d.esperado}.label`)
        setError(d.es
          ? t('system.import.errors.otroTablero', {
              esperado, es: t(`system.import.tipos.${d.es}.label`), paso: IMPORT_TIPOS.indexOf(d.es) + 1 })
          : d.tablero
            ? t('system.import.errors.noSeImporta', { esperado, tablero: d.tablero })
            : t('system.import.errors.noEs', { esperado }))
      } else {
        setError(typeof d === 'string' ? d : t('system.import.failed'))
      }
    }
    setBusy(false)
  }

  const listo = !!archivo

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-2xl">
      <h2 className="text-sm font-semibold text-gray-900 mb-1">{t('system.import.title')}</h2>
      <p className="text-xs text-gray-500 mb-4">{t('system.import.intro')}</p>

      <div className="flex flex-wrap gap-2 mb-2">
        {IMPORT_TIPOS.map((k, i) => (
          <button
            key={k}
            onClick={cambiar(() => { setTipo(k); setReemplazar(false) })}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
              tipo === k ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {i + 1}. {t(`system.import.tipos.${k}.label`)}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500 mb-4">{t(`system.import.tipos.${tipo}.help`)}</p>

      <div className="space-y-3 mb-4">
        <input
          key={inputKey}
          type="file"
          accept=".xlsx"
          onChange={cambiar(e => setArchivo(e.target.files?.[0] || null))}
          className="block w-full text-sm text-gray-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-gray-100 file:text-sm file:text-gray-700 hover:file:bg-gray-200"
        />
        {tipo === 'partes' && (
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" className="mt-0.5" checked={reemplazar}
                   onChange={cambiar(e => setReemplazar(e.target.checked))} />
            <span>
              {t('system.import.reemplazar')}
              <span className="block text-xs text-red-600">{t('system.import.reemplazarHelp')}</span>
            </span>
          </label>
        )}
      </div>

      {error && <p className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}

      {!preview && (
        <button
          onClick={() => enviar(false)}
          disabled={!listo || busy}
          className="bg-gray-900 text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors"
        >
          {busy ? t('system.import.working') : t('system.import.preview')}
        </button>
      )}

      {preview && (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">{t('system.import.previewIntro')}</p>
          <ImportResumen resumen={preview} />
          <p className="text-xs text-gray-500">{t('system.import.backupHint')}</p>
          <div className="flex gap-2">
            <button
              onClick={() => enviar(true)}
              disabled={busy}
              className={`text-white text-sm px-4 py-2 rounded-lg font-medium disabled:opacity-50 transition-colors ${
                reemplazar ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              {busy ? t('system.import.working') : t('system.import.apply')}
            </button>
            <button
              onClick={() => setPreview(null)}
              disabled={busy}
              className="text-sm px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100"
            >
              {t('common:actions.cancel', { defaultValue: 'Cancelar' })}
            </button>
          </div>
        </div>
      )}

      {aplicado && (
        <div className="space-y-3 mt-2">
          <p className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
            {t('system.import.done')}
          </p>
          <ImportResumen resumen={aplicado} />
        </div>
      )}
    </div>
  )
}

// ── Alerts tab ───────────────────────────────────────────────────────────────

const EMPTY_ALERT = { title: '', body: '', expires_at: '', is_active: true }

function alertStatus(a) {
  if (!a.is_active) return 'inactive'
  if (a.expires_at) {
    // expires_at is naive UTC from the backend; parse it as UTC so this badge
    // agrees with the server's active-filter (which compares to utcnow()).
    const iso = a.expires_at.endsWith('Z') ? a.expires_at : `${a.expires_at}Z`
    if (new Date(iso) <= new Date()) return 'expired'
  }
  return 'active'
}

function AlertsTab() {
  const { t } = useTranslation(['settings', 'common'])
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)   // 'new' | alert object | null
  const [form, setForm] = useState(EMPTY_ALERT)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true)
    try { setAlerts(await getAlerts()) } catch (_) {}
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  const openNew = () => { setForm(EMPTY_ALERT); setError(''); setModal('new') }
  const openEdit = (a) => {
    setForm({
      title: a.title || '',
      body: a.body || '',
      expires_at: a.expires_at ? a.expires_at.slice(0, 10) : '',
      is_active: a.is_active,
    })
    setError('')
    setModal(a)
  }

  const handleSave = async () => {
    if (!form.title.trim()) { setError(t('alerts.modal.titleRequired')); return }
    setSaving(true); setError('')
    // A date-only input means "show through the end of that day".
    const payload = {
      title: form.title,
      body: form.body || null,
      is_active: form.is_active,
      expires_at: form.expires_at ? `${form.expires_at}T23:59:59` : null,
    }
    try {
      if (modal === 'new') await createAlert(payload)
      else await updateAlert(modal.id, payload)
      await load()
      setModal(null)
    } catch (e) {
      setError(e.response?.data?.detail || t('alerts.modal.saveFailed'))
    }
    setSaving(false)
  }

  const handleToggle = async (a) => {
    try { await updateAlert(a.id, { is_active: !a.is_active }); await load() } catch (_) {}
  }

  const handleDelete = async (a) => {
    if (!confirm(t('alerts.deleteConfirm'))) return
    try { await deleteAlert(a.id); await load() } catch (_) {}
  }

  const STATUS_BADGE = {
    active:   'bg-emerald-100 text-emerald-700',
    inactive: 'bg-gray-100 text-gray-500',
    expired:  'bg-amber-100 text-amber-700',
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-gray-400">{t('alerts.intro')}</p>
        <button onClick={openNew}
          className="bg-gray-900 text-white text-sm px-3 py-1.5 rounded-lg font-medium hover:bg-gray-700 transition-colors">
          {t('alerts.newButton')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <p className="text-sm text-gray-400 px-6 py-8">{t('alerts.loading')}</p>
        ) : alerts.length === 0 ? (
          <p className="text-sm text-gray-400 px-6 py-8">{t('alerts.empty')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('alerts.columns.title')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('alerts.columns.status')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('alerts.columns.expires')}</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {alerts.map(a => {
                const st = alertStatus(a)
                return (
                  <tr key={a.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors last:border-b-0">
                    <td className="px-5 py-3">
                      <p className="font-medium text-gray-900">{a.title}</p>
                      {a.body && <p className="text-xs text-gray-400 truncate max-w-md">{a.body}</p>}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[st]}`}>
                        {t(`alerts.status.${st}`)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-gray-500">
                      {a.expires_at ? a.expires_at.slice(0, 10) : t('alerts.noExpiry')}
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button onClick={() => handleToggle(a)} className="text-xs text-gray-400 hover:text-gray-900 mr-3">
                        {a.is_active ? t('alerts.status.inactive') : t('alerts.status.active')}
                      </button>
                      <button onClick={() => openEdit(a)} className="text-xs text-gray-400 hover:text-gray-900 mr-3">{t('common:actions.edit')}</button>
                      <button onClick={() => handleDelete(a)} className="text-xs text-gray-400 hover:text-red-500">{t('common:actions.delete')}</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setModal(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-5">
              {modal === 'new' ? t('alerts.modal.createTitle') : t('alerts.modal.editTitle')}
            </h2>

            {error && (
              <p className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('alerts.modal.title')}</label>
                <input autoFocus
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('alerts.modal.body')}</label>
                <textarea rows={4}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                  value={form.body}
                  placeholder={t('alerts.modal.bodyPlaceholder')}
                  onChange={e => setForm(f => ({ ...f, body: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('alerts.modal.expiresAt')}</label>
                <input type="date"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                  value={form.expires_at}
                  onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
              </div>
              <label className="flex items-center gap-2.5 text-sm text-gray-700">
                <input type="checkbox" className="accent-gray-900"
                  checked={form.is_active}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                {t('alerts.modal.isActive')}
              </label>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSave} disabled={saving}
                className="flex-1 bg-gray-900 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-40 transition-colors">
                {saving ? t('common:actions.saving') : t('alerts.modal.save')}
              </button>
              <button onClick={() => setModal(null)}
                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
                {t('common:actions.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
